import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { attendanceStructureService, payrollPolicyService } from "../../../../services/api";
import { DsButton } from "../../../../design-system";
import { PolicyHintField } from "../../policy-wizard/PolicyFieldHint";
import { estimateEmi, firstDeductionMonthLabel } from "../../../../utils/loanApplicationCalc";
import { pwInput } from "../../payroll-settings/payrollJsonPolicyForm";
import { ACTION_MODAL_OVERLAY, ACTION_MODAL_SHELL } from "../../shared/actionUiChrome";

function empName(employee) {
  return (
    [employee?.empCode || employee?.employeeCode, employee?.user?.name, employee?.user?.lastName]
      .filter(Boolean)
      .join(" ") || "Employee"
  );
}

export default function LoanEmployeeModal({ isOpen, onClose, employee, darkMode = false }) {
  void darkMode;
  const [policies, setPolicies] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyId, setPolicyId] = useState("");
  const [amount, setAmount] = useState("");
  const [tenureMonths, setTenureMonths] = useState("");
  const [purpose, setPurpose] = useState("");
  const [reason, setReason] = useState("");
  const [disbursementDate, setDisbursementDate] = useState("");

  const load = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        payrollPolicyService.listLoanPolicies({ employeeId: employee.id }),
        payrollPolicyService.listLoanApplications({ employeeId: employee.id }),
      ]);
      const list = Array.isArray(pRes?.data) ? pRes.data : [];
      setPolicies(list);
      setApps(Array.isArray(aRes?.data) ? aRes.data : []);
      const firstOk = list.find((p) => p.match?.eligible);
      setPolicyId(String(firstOk?.id || list[0]?.id || ""));
    } catch {
      setPolicies([]);
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !employee?.id) return undefined;
    load();
    return undefined;
  }, [isOpen, employee?.id]);

  const policy = useMemo(
    () => policies.find((p) => String(p.id) === String(policyId)) || null,
    [policies, policyId]
  );
  const rules = policy?.rulesJson || {};
  const emi = estimateEmi(amount, tenureMonths, rules.interestMode, rules.interestRateAnnual);

  const submit = async () => {
    if (!policy) {
      toast.error("Select a loan policy");
      return;
    }
    if (!policy.match?.eligible) {
      toast.error("Employee is not eligible under this policy");
      return;
    }
    setSaving(true);
    try {
      const res = await payrollPolicyService.createLoanApplication({
        employeeId: employee.id,
        policyId: policy.id,
        amount: Number(amount),
        tenureMonths: Number(tenureMonths),
        purpose: purpose || policy.loanType,
        reason,
        requestedDisbursementDate: disbursementDate || null,
      });
      if (res?.success === false) throw new Error(res.message || "Submit failed");
      toast.success("Loan application submitted");
      setAmount("");
      setTenureMonths("");
      setPurpose("");
      setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className={ACTION_MODAL_OVERLAY}>
      <div className={`${ACTION_MODAL_SHELL} max-w-2xl`}>
        <div className="flex items-start justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h3 className="text-base font-semibold">Loan application</h3>
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">{empName(employee)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 opacity-70">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
          {loading ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <PolicyHintField
                  label="Loan policy"
                  hint="Only active policies that match this employee can be submitted."
                >
                  <select
                    className={pwInput}
                    value={policyId}
                    onChange={(e) => setPolicyId(e.target.value)}
                  >
                    <option value="">Select policy</option>
                    {policies.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.match?.eligible ? "" : " (not eligible)"}
                      </option>
                    ))}
                  </select>
                </PolicyHintField>
                <PolicyHintField label="Loan type" hint="Taken from the selected policy. Not 80C / housing loan in Tax Planning.">
                  <input className={pwInput} readOnly value={policy?.loanType || policy?.rulesJson?.loanType || "—"} />
                </PolicyHintField>
                <PolicyHintField label="Requested amount (₹)" hint="Must sit between the policy min and max.">
                  <input
                    type="number"
                    min="0"
                    className={pwInput}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </PolicyHintField>
                <PolicyHintField
                  label="Tenure (months)"
                  hint={`Allowed range ${rules.minTenureMonths || "—"}–${rules.maxTenureMonths || "—"} months.`}
                >
                  <input
                    type="number"
                    min="1"
                    className={pwInput}
                    value={tenureMonths}
                    onChange={(e) => setTenureMonths(e.target.value)}
                  />
                </PolicyHintField>
                <PolicyHintField label="Purpose" hint="Short purpose for this application (medical, education, etc.).">
                  <input className={pwInput} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
                </PolicyHintField>
                <PolicyHintField
                  label="Requested disbursement date"
                  hint="Recorded on the application. Payroll EMI starts after sanction + first-deduction delay."
                >
                  <input
                    type="date"
                    className={pwInput}
                    value={disbursementDate}
                    onChange={(e) => setDisbursementDate(e.target.value)}
                  />
                </PolicyHintField>
                <PolicyHintField
                  label="Estimated EMI (₹)"
                  hint="Preview from policy interest mode. Final EMI is set on sanction."
                >
                  <input className={pwInput} readOnly value={emi ? emi.toLocaleString("en-IN") : "—"} />
                </PolicyHintField>
                <PolicyHintField
                  label="First deduction month (preview)"
                  hint="Based on policy delay from this month. Sanctioned first month may differ."
                >
                  <input
                    className={pwInput}
                    readOnly
                    value={firstDeductionMonthLabel(rules.firstDeductionDelayMonths)}
                  />
                </PolicyHintField>
                <div className="sm:col-span-2">
                  <PolicyHintField label="Reason / remarks" hint="Shown to approvers. Required if the policy workflow needs a reason on reject.">
                    <textarea
                      className={`${pwInput} min-h-[72px]`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </PolicyHintField>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Previous applications
                </h4>
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-[hsl(var(--ds-border))]">
                      <th className="py-2">Date</th>
                      <th>Amount</th>
                      <th>Tenure</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((a) => (
                      <tr key={a.id} className="border-b border-[hsl(var(--ds-border))]">
                        <td className="py-2">{String(a.createdAt || "").slice(0, 10)}</td>
                        <td>{Number(a.amount || 0).toLocaleString("en-IN")}</td>
                        <td>{a.tenureMonths}</td>
                        <td className="capitalize">{a.status}</td>
                      </tr>
                    ))}
                    {!apps.length && (
                      <tr>
                        <td colSpan={4} className="py-3 text-[hsl(var(--ds-muted-fg))]">
                          No applications yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="outline" onClick={onClose}>
            Close
          </DsButton>
          <DsButton type="button" onClick={submit} disabled={saving || loading}>
            Submit application
          </DsButton>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function LeaveEncashmentEmployeeModal({ isOpen, onClose, employee, darkMode = false }) {
  void darkMode;
  const [policies, setPolicies] = useState([]);
  const [rows, setRows] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyId, setPolicyId] = useState("");
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [days, setDays] = useState("");
  const [payoutMode, setPayoutMode] = useState("with_payroll");
  const [payMonth, setPayMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [reason, setReason] = useState("");

  const load = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const [pRes, rRes, tRes] = await Promise.all([
        payrollPolicyService.listLeaveEncashmentPolicies({ employeeId: employee.id }),
        payrollPolicyService.listLeaveEncashmentRequests({ employeeId: employee.id }),
        attendanceStructureService.getEmployeeLeaveTypes(employee.id).catch(() => null),
      ]);
      const list = Array.isArray(pRes?.data) ? pRes.data : [];
      setPolicies(list);
      setRows(Array.isArray(rRes?.data) ? rRes.data : []);
      const types = tRes?.data || tRes?.leaveTypes || (Array.isArray(tRes) ? tRes : []);
      setLeaveTypes(Array.isArray(types) ? types : []);
      const firstOk = list.find((p) => p.match?.eligible);
      setPolicyId(String(firstOk?.id || list[0]?.id || ""));
    } catch {
      setPolicies([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !employee?.id) return undefined;
    load();
    return undefined;
  }, [isOpen, employee?.id]);

  const policy = useMemo(
    () => policies.find((p) => String(p.id) === String(policyId)) || null,
    [policies, policyId]
  );
  const rules = policy?.rulesJson || {};

  const submit = async () => {
    if (!policy) {
      toast.error("Select a policy");
      return;
    }
    if (!policy.match?.eligible) {
      toast.error("Employee is not eligible under this policy");
      return;
    }
    setSaving(true);
    try {
      const res = await payrollPolicyService.createLeaveEncashmentRequest({
        employeeId: employee.id,
        policyId: policy.id,
        leaveTypeId: leaveTypeId ? Number(leaveTypeId) : null,
        days: Number(days),
        payMonth,
        payoutMode,
        reason,
      });
      if (res?.success === false) throw new Error(res.message || "Submit failed");
      toast.success(
        payoutMode === "away_from_payroll"
          ? "Leave encashment saved for separate disbursement"
          : "Leave encashment saved for payroll"
      );
      setDays("");
      setReason("");
      setPayoutMode("with_payroll");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className={ACTION_MODAL_OVERLAY}>
      <div className={`${ACTION_MODAL_SHELL} max-w-2xl`}>
        <div className="flex items-start justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h3 className="text-base font-semibold">Leave encashment</h3>
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">{empName(employee)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 opacity-70">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
          {loading ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</p>
          ) : (
            <>
              <div className="flex justify-end">
                <span
                  title="Records leave encashment for payout. With payroll adds the earning in Process for the pay month. Away from payroll goes to the separate Leave Encashment disbursement register (not Process Gross). Amount = policy daily rate × days. Approval workflow is deferred — new rows are payable immediately."
                  className="cursor-help text-[hsl(var(--ds-muted-fg))] text-sm"
                >
                  ⓘ
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <PolicyHintField
                  label="Encashment policy"
                  hint="Leave Group must allow encashment. This policy adds days, pay month and tax."
                >
                  <select
                    className={pwInput}
                    value={policyId}
                    onChange={(e) => setPolicyId(e.target.value)}
                  >
                    <option value="">Select policy</option>
                    {policies.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.match?.eligible ? "" : " (not eligible)"}
                      </option>
                    ))}
                  </select>
                </PolicyHintField>
                <PolicyHintField label="Leave type" hint="Only types with Encashment Allowed on Leave Group should be used.">
                  <select
                    className={pwInput}
                    value={leaveTypeId}
                    onChange={(e) => setLeaveTypeId(e.target.value)}
                  >
                    <option value="">Select leave type</option>
                    {leaveTypes.map((t) => (
                      <option key={t.id || t.leaveTypeId} value={t.id || t.leaveTypeId}>
                        {t.name || t.leaveTypeName || t.code}
                      </option>
                    ))}
                  </select>
                </PolicyHintField>
                <PolicyHintField
                  label="Days to encash"
                  hint={`Min ${rules.minEncashDays ?? 1}${rules.maxEncashDays ? `, max ${rules.maxEncashDays}` : ""}. Keep at least ${rules.minBalanceDays ?? 0} days.`}
                >
                  <input
                    type="number"
                    min="1"
                    className={pwInput}
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                  />
                </PolicyHintField>
                <PolicyHintField
                  label="Pay month"
                  hint="Must be an unfinalised payroll month. Process Payroll posts the earning then."
                >
                  <input
                    type="month"
                    className={pwInput}
                    value={payMonth}
                    onChange={(e) => setPayMonth(e.target.value)}
                  />
                </PolicyHintField>
                <div>
                  <label className="mb-1 block text-xs font-medium">Payout mode</label>
                  <select
                    className={pwInput}
                    value={payoutMode}
                    onChange={(e) => setPayoutMode(e.target.value)}
                  >
                    <option value="with_payroll">With payroll</option>
                    <option value="away_from_payroll">Away from payroll</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <PolicyHintField label="Reason / remarks" hint="Shown to approvers.">
                    <textarea
                      className={`${pwInput} min-h-[72px]`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </PolicyHintField>
                </div>
              </div>
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[hsl(var(--ds-border))]">
                    <th className="py-2">Date</th>
                    <th>Days</th>
                    <th>Amount</th>
                    <th>Mode</th>
                    <th>Pay month</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-[hsl(var(--ds-border))]">
                      <td className="py-2">{String(r.createdAt || "").slice(0, 10)}</td>
                      <td>{r.days}</td>
                      <td>{r.amount != null ? Number(r.amount).toFixed(2) : "—"}</td>
                      <td>
                        {r.payoutMode === "away_from_payroll" ? "Away" : "With payroll"}
                      </td>
                      <td>{String(r.payMonth || "").slice(0, 7)}</td>
                      <td className="capitalize">{r.status}</td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={6} className="py-3 text-[hsl(var(--ds-muted-fg))]">
                        No encashments yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="outline" onClick={onClose}>
            Close
          </DsButton>
          <DsButton type="button" onClick={submit} disabled={saving || loading}>
            Save encashment
          </DsButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
